import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import { Button, Checkbox, Dropdown, Form, Header, Input, Modal } from "semantic-ui-react";

import { DBB_CONTRACT_TYPE } from "./../../../../modules/Appointments/common";
import AppService from "./../../../../modules/Appointments/service";
import "./DBBCreateContractModal.css";

const APPOINTMENT_CUSTOMER_FIELDS = ["customer_owner", "customer_contract", "customer_driver"];

class DBBCreateContractModal extends Component {
  state = {
    contractType: this.props.appointment.is_lease_company ? DBB_CONTRACT_TYPE.LEASE : DBB_CONTRACT_TYPE.PRIVATE,
    customer: { id: null, isUnknown: false },
    driver: { id: null, isUnknown: false },
    owner: { id: null, isUnknown: false },
    isSaving: false,
  };

  getContractTypeOptions = () => {
    const { t } = this.props;

    return [
      { key: DBB_CONTRACT_TYPE.PRIVATE, value: DBB_CONTRACT_TYPE.PRIVATE, text: t("private").message || "Private" },
      { key: DBB_CONTRACT_TYPE.LEASE, value: DBB_CONTRACT_TYPE.LEASE, text: t("lease").message || "Lease" },
      { key: DBB_CONTRACT_TYPE.FLEET, value: DBB_CONTRACT_TYPE.FLEET, text: t("fleet").message || "Fleet" },
    ];
  };

  getContractSections = () => {
    const { t } = this.props;
    const { contractType } = this.state;

    const sections = {
      customer: { customerField: "customer", label: t("customer").message || "Customer" },
      driver: { customerField: "driver", label: t("driver").message || "Driver" },
      owner: { customerField: "owner", label: t("owner").message || "Owner" },
    };

    if (contractType === DBB_CONTRACT_TYPE.LEASE) return [sections.customer, sections.driver];
    if (contractType === DBB_CONTRACT_TYPE.FLEET) return [sections.owner, sections.driver];

    return [sections.owner];
  };

  getAppointmentCustomers = () => {
    const { appointment } = this.props;

    return APPOINTMENT_CUSTOMER_FIELDS.reduce((customers, customerField) => {
      const customer = appointment[customerField];
      if (customer && !customers.some(({ id }) => id === customer.id)) customers.push(customer);

      return customers;
    }, []);
  };

  getCustomerOptions = appointmentCustomers =>
    appointmentCustomers.map(customer => ({
      key: customer.id,
      value: customer.id,
      text: `${customer.firstname || ""} ${customer.surname || ""}`.trim() || customer.company,
    }));

  hasIncompleteSection = sections => sections.some(({ customerField }) => !this.state[customerField].isUnknown && !this.state[customerField].id);

  handleChangeContractType = contractType => this.setState({ contractType });

  handleChangeCustomer = (customerField, id) => this.setState(state => ({ [customerField]: { ...state[customerField], id } }));

  handleToggleUnknown = customerField => this.setState(state => ({ [customerField]: { ...state[customerField], isUnknown: !state[customerField].isUnknown } }));

  handleSave = () => {
    const { appointment, onCarUpdate, onError, onClose } = this.props;
    const { contractType } = this.state;

    const requestData = { appointment_id: appointment.id, contract_type: contractType };

    this.getContractSections().forEach(({ customerField }) => {
      if (this.state[customerField].isUnknown) return;

      requestData[customerField] = this.state[customerField].id;
    });

    this.setState({ isSaving: true });

    AppService.createDBBContract(requestData)
      .then(result => {
        this.setState({ isSaving: false });

        if (!result?.data?.data) return onError("An unknown error occured, please try again");

        onCarUpdate({ ...appointment.car, ...result.data.data });
        onClose();
      })
      .catch(error => {
        this.setState({ isSaving: false });
        const errorMsg = error.response?.data?.errors?.[0] || "Error creating DBB contract";
        onError(errorMsg);
        console.warn("Error creating DBB contract", error);
      });
  };

  renderCustomerDetails = (customerId, appointmentCustomers) => {
    const { t } = this.props;
    const customer = appointmentCustomers.find(({ id }) => id === customerId);

    if (!customer) return null;

    return (
      <>
        <Form.Group widths="equal">
          <Form.Field>
            <label>{t("company").message || "Company"}</label>
            <Input value={customer.company || ""} readOnly disabled />
          </Form.Field>
          <Form.Field>
            <label>{t("street").message || "Street"}</label>
            <Input value={customer.street || ""} readOnly disabled />
          </Form.Field>
          <Form.Field>
            <label>{t("postcode").message || "Postcode"}</label>
            <Input value={customer.postcode || ""} readOnly disabled />
          </Form.Field>
        </Form.Group>
        <Form.Group widths="equal">
          <Form.Field>
            <label>{t("city").message || "City"}</label>
            <Input value={customer.place || ""} readOnly disabled />
          </Form.Field>
          <Form.Field>
            <label>{t("email").message || "Email"}</label>
            <Input value={customer.email_business || ""} readOnly disabled />
          </Form.Field>
          <Form.Field>
            <label>{t("mobile_phone_number").message || "Mobile phone number"}</label>
            <Input value={customer.tel_mobile_business || ""} readOnly disabled />
          </Form.Field>
        </Form.Group>
      </>
    );
  };

  renderCustomerSection = ({ customerField, label }, appointmentCustomers, customerOptions) => {
    const { t } = this.props;
    const { isUnknown, id } = this.state[customerField];

    return (
      <div className="customer-section" key={customerField}>
        <Header as="h5" dividing>
          {label}
        </Header>

        <Form.Field>
          <Checkbox label={t("unknown").message || "Unknown"} checked={isUnknown} onChange={() => this.handleToggleUnknown(customerField)} />
        </Form.Field>

        {!isUnknown && (
          <>
            <Form.Field>
              <label>{t("customer").message || "Customer"}</label>
              <Dropdown
                selection
                fluid
                selectOnBlur={false}
                placeholder={t("select_customer").message || "Select customer"}
                value={id}
                options={customerOptions}
                onChange={(_e, { value }) => this.handleChangeCustomer(customerField, value)}
              />
            </Form.Field>

            {this.renderCustomerDetails(id, appointmentCustomers)}
          </>
        )}
      </div>
    );
  };

  render() {
    const { open, onClose, t } = this.props;
    const { contractType, isSaving } = this.state;

    const sections = this.getContractSections();
    const appointmentCustomers = this.getAppointmentCustomers();
    const customerOptions = this.getCustomerOptions(appointmentCustomers);

    return (
      <Modal size="large" open={open} onClose={onClose} closeOnDimmerClick={false} className="DBBCreateContractModal">
        <Modal.Header>{t("create_dbb_contract").message || "Create DBB contract"}</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field>
              <label>{t("contract_type").message || "Contract type"}</label>
              <Dropdown
                selection
                fluid
                selectOnBlur={false}
                value={contractType}
                options={this.getContractTypeOptions()}
                onChange={(_e, { value }) => this.handleChangeContractType(value)}
              />
            </Form.Field>

            {sections.map(section => this.renderCustomerSection(section, appointmentCustomers, customerOptions))}
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button color="grey" onClick={onClose}>
            {t("cancel").message || "Cancel"}
          </Button>
          <Button color="green" onClick={this.handleSave} disabled={isSaving || this.hasIncompleteSection(sections)} loading={isSaving}>
            {t("save").message || "Save"}
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

export default withTranslation()(DBBCreateContractModal);
